/**
 * MicrosoftConnectCard.jsx — Vague 113 L11 (Phase 2 Email).
 *
 * Compact card displayed in Paramètres → Boîte email that allows the user
 * to connect/disconnect their Microsoft (Outlook + Calendar) account.
 *
 * 3 states:
 *  - Not configured (admin hasn't set MICROSOFT_CLIENT_ID/_SECRET env vars):
 *    grey card with "Microsoft Outlook non configuré — contactez votre admin"
 *  - Configured but not connected: "Connecter Outlook" button
 *  - Connected: display email + "Déconnecter" button
 *
 * URL params after callback (`?microsoft=connected&email=...` or
 * `?microsoft=error&detail=...`) are read on mount and shown as a toast.
 */
import React, { useEffect, useState } from 'react';
import { Mail, CheckCircle2, AlertCircle, Loader2, Link as LinkIcon, ShieldCheck, Copy, ExternalLink, Building2 } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { oauthRedirect } from '../utils/oauthRedirect';

// Tiny relative time formatter — "il y a 2 min", "il y a 1 h", "il y a 3 j"
function _fmtRelative(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "à l'instant";
    if (ms < 3_600_000) return `il y a ${Math.round(ms / 60_000)} min`;
    if (ms < 86_400_000) return `il y a ${Math.round(ms / 3_600_000)} h`;
    return `il y a ${Math.round(ms / 86_400_000)} j`;
  } catch { return ''; }
}

export default function MicrosoftConnectCard() {
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Wave 1g — send-default toggle
  const [prefs, setPrefs] = useState(null);
  const [accounts, setAccounts] = useState([]);
  // Wave 1h — onboarding sync (calendar + realtime push)
  const [syncing, setSyncing] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  // Wave 1u3 Phase D — admin consent for tenant-wide Mail.ReadWrite approval
  const [consentStatus, setConsentStatus] = useState({ consents: [] });
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentCopied, setConsentCopied] = useState(false);

  const reloadConsent = async () => {
    try {
      const { data } = await api.get('/integrations/microsoft/admin-consent-status');
      setConsentStatus(data || { consents: [] });
    } catch {
      setConsentStatus({ consents: [] });
    }
  };

  const buildConsentLink = async () => {
    setConsentBusy(true);
    try {
      const { data } = await api.post('/integrations/microsoft/admin-consent-url', {
        return_origin: window.location.origin,
      });
      return data?.auth_url;
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Génération du lien admin-consent échouée' });
      return null;
    } finally {
      setConsentBusy(false);
    }
  };

  const launchAdminConsent = async () => {
    const url = await buildConsentLink();
    if (url) oauthRedirect(url);
  };

  const copyAdminLink = async () => {
    const url = await buildConsentLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setConsentCopied(true);
      setTimeout(() => setConsentCopied(false), 4000);
      toast({ title: 'Lien copié', description: 'Envoyez-le à votre administrateur Microsoft 365.' });
    } catch {
      window.prompt('Copiez ce lien pour votre admin :', url);
    }
  };

  const reloadPush = async () => {
    try {
      const { data } = await api.get('/integrations/microsoft/push/status');
      setPushStatus(data);
    } catch {
      setPushStatus({ active: false });
    }
  };

  const reload = async () => {
    let connected = false;
    try {
      const { data } = await api.get('/integrations/microsoft/status');
      setStatus(data);
      connected = !!data?.connected;
    } catch {
      setStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
    // Also fetch mailbox prefs + available accounts
    try {
      const [{ data: p }, { data: m }] = await Promise.all([
        api.get('/mailbox/preferences'),
        api.get('/mailbox/status'),
      ]);
      setPrefs(p);
      setAccounts(m?.accounts || []);
    } catch { /* prefs optional */ }
    if (connected) {
      reloadPush();
    } else {
      setPushStatus(null);
    }
  };

  const syncCalendar = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/integrations/microsoft/calendar/pull');
      toast({
        title: 'Calendrier Outlook synchronisé',
        description: `${data?.synced ?? 0} événements importés`,
      });
    } catch (err) {
      toast({
        title: 'Échec de la synchronisation',
        description: err?.response?.data?.detail || 'Erreur inconnue',
      });
    } finally { setSyncing(false); }
  };

  const startRealtime = async () => {
    setPushBusy(true);
    try {
      await api.post('/integrations/microsoft/push/start');
      toast({ title: 'Notifications temps réel activées' });
      await reloadPush();
    } catch (err) {
      toast({
        title: 'Activation impossible',
        description: err?.response?.data?.detail || 'URL push non joignable depuis Microsoft',
      });
    } finally { setPushBusy(false); }
  };

  const stopRealtime = async () => {
    if (!window.confirm('Désactiver les notifications temps réel Outlook ?')) return;
    setPushBusy(true);
    try {
      await api.post('/integrations/microsoft/push/stop');
      toast({ title: 'Notifications temps réel désactivées' });
      await reloadPush();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Échec',
      });
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    reload();
    reloadConsent();
    // Consume callback query params from /app/parametres?microsoft=...
    const params = new URLSearchParams(window.location.search);
    const code = params.get('microsoft');
    if (code === 'connected') {
      const email = params.get('email');
      toast({ title: 'Compte Outlook lié', description: email || 'Connexion réussie' });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (code === 'error') {
      const detail = params.get('detail');
      toast({ title: 'Échec de connexion Outlook', description: detail || 'Erreur inconnue' });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Wave 1u3 Phase D — also consume the admin-consent callback redirect
    const consentCode = params.get('ms_admin_consent');
    if (consentCode === 'ok') {
      const tenantId = params.get('tenant') || '';
      toast({
        title: 'Microsoft activé pour votre organisation',
        description: tenantId ? `Tenant : ${tenantId}. Tous les utilisateurs de votre OF peuvent maintenant synchroniser leurs emails.` : 'Permissions accordées par l\'administrateur.',
      });
      reloadConsent();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (consentCode === 'error') {
      const detail = params.get('detail');
      toast({ title: 'Admin-consent refusé ou échoué', description: detail || 'Erreur inconnue' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      // Pass return_origin so the backend builds a redirect_uri that EXACTLY
      // matches one of the Web redirect URIs registered in Azure AD.
      const { data } = await api.get('/integrations/microsoft/start', {
        params: { return_origin: window.location.origin },
      });
      if (data?.auth_url) oauthRedirect(data.auth_url, { onAfterPopup: () => setBusy(false) });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Démarrage OAuth échoué' });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter le compte Outlook ?')) return;
    setBusy(true);
    try {
      await api.post('/integrations/microsoft/disconnect');
      toast({ title: 'Compte Outlook déconnecté' });
      await reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setBusy(false); }
  };

  const setSendBackend = async (backend) => {
    try {
      const { data } = await api.put('/mailbox/preferences', { preferred_send_backend: backend });
      setPrefs(data);
      toast({ title: 'Préférence enregistrée', description: backend === 'auto' ? 'Sélection automatique' : `Envoi via ${backend}` });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-[#EFF0F6] bg-white p-4" data-testid="microsoft-connect-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#6B6C85]" />
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-[#EFF0F6] bg-white p-4"
      data-testid="microsoft-connect-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-[#0078D4]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[13.5px] font-bold text-[#1A1B2E]">Microsoft Outlook & Calendar</div>
            {status.connected ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#ECFDF5] text-[#065F46]">
                <CheckCircle2 className="w-2.5 h-2.5" /> Connecté
              </span>
            ) : status.configured ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F6F7FB] text-[#6B6C85]">
                Non connecté
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#FEF3C7] text-[#92400E]">
                <AlertCircle className="w-2.5 h-2.5" /> Non configuré
              </span>
            )}
          </div>
          {status.connected ? (
            <>
              <div className="text-[12px] text-[#4A4B63] mt-0.5">{status.email}</div>
              {status.display_name && <div className="text-[11px] text-[#8E8FA5]">{status.display_name}</div>}
              <div className="text-[10.5px] text-[#8E8FA5] italic mt-1">
                Lecture, envoi, calendrier · Compte {status.account_type === 'personal' ? 'personnel Outlook.com' : 'Microsoft 365'}
              </div>
              {status.last_calendar_pull_at && (
                <div className="text-[10.5px] text-[#15803D] mt-0.5" data-testid="microsoft-last-sync">
                  ✓ Calendrier synchronisé {_fmtRelative(status.last_calendar_pull_at)}
                </div>
              )}
              {/* Wave 1h — onboarding sync actions */}
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="microsoft-sync-actions">
                <button
                  onClick={syncCalendar}
                  disabled={syncing}
                  data-testid="microsoft-sync-calendar-btn"
                  className="h-7 px-2.5 rounded-md text-[11px] font-semibold border border-[#C7D2FE] bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#E0E7FF] disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : '📅'}
                  Synchroniser le calendrier
                </button>
                {pushStatus?.active ? (
                  <button
                    onClick={stopRealtime}
                    disabled={pushBusy}
                    data-testid="microsoft-stop-realtime-btn"
                    className="h-7 px-2.5 rounded-md text-[11px] font-semibold border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] hover:bg-[#D1FAE5] disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    ✓ Temps réel actif · Désactiver
                  </button>
                ) : (
                  <button
                    onClick={startRealtime}
                    disabled={pushBusy}
                    data-testid="microsoft-start-realtime-btn"
                    className="h-7 px-2.5 rounded-md text-[11px] font-semibold border border-[#EFF0F6] bg-white text-[#4A4B63] hover:bg-[#FAFAFE] disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    {pushBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : '⚡'}
                    Activer notifications temps réel
                  </button>
                )}
              </div>
            </>
          ) : status.configured ? (
            <div className="text-[12px] text-[#6B6C85] mt-1">
              Liez votre compte Outlook (personnel ou Microsoft 365) pour lire/envoyer vos emails et synchroniser votre calendrier.
            </div>
          ) : (
            <div className="text-[12px] text-[#92400E] mt-1">
              L'administrateur OFManager doit d'abord créer une application Azure AD et ajouter <code className="text-[11px] bg-[#FEF3C7] px-1 rounded">MICROSOFT_CLIENT_ID</code> + <code className="text-[11px] bg-[#FEF3C7] px-1 rounded">MICROSOFT_CLIENT_SECRET</code> dans la configuration du serveur.
            </div>
          )}
        </div>
        <div className="flex-shrink-0">
          {status.connected ? (
            <button
              onClick={disconnect}
              disabled={busy}
              data-testid="microsoft-disconnect-btn"
              className="h-9 px-3 rounded-lg border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2] text-[12px] font-semibold disabled:opacity-50"
            >
              Déconnecter
            </button>
          ) : status.configured ? (
            <button
              onClick={connect}
              disabled={busy}
              data-testid="microsoft-connect-btn"
              className="h-9 px-3 rounded-lg bg-[#0078D4] text-white text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#106EBE] disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />}
              Connecter Outlook
            </button>
          ) : null}
        </div>
      </div>

      {/* Wave 1u3 Phase D — Admin consent banner for tenant-wide activation */}
      {status.configured && (
        <AdminConsentBanner
          consents={consentStatus.consents}
          busy={consentBusy}
          copied={consentCopied}
          onLaunch={launchAdminConsent}
          onCopy={copyAdminLink}
        />
      )}

      {/* Wave 1g — Send-default selector (visible when ≥ 2 backends connected) */}
      {prefs && accounts.filter((a) => a.can_send).length >= 2 && (
        <div className="mt-3 pt-3 border-t border-[#F3F4F8]" data-testid="mailbox-send-default-selector">
          <label className="block text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
            Compte par défaut pour l'envoi
          </label>
          <select
            value={prefs.preferred_send_backend || 'auto'}
            onChange={(e) => setSendBackend(e.target.value)}
            data-testid="mailbox-send-default-select"
            className="w-full h-9 px-2 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px]"
          >
            <option value="auto">🤖 Automatique (SMTP &gt; Microsoft &gt; Gmail)</option>
            {accounts.filter((a) => a.can_send).map((a) => {
              const label = a.provider === 'google'
                ? `🟢 Gmail (${a.email || ''})`
                : a.provider === 'microsoft'
                  ? `🔵 Microsoft (${a.email || ''})`
                  : a.provider === 'imap'
                    ? `✉️ SMTP de Paramètres (${a.email || ''})`
                    : `📨 ${a.label || a.email}`;
              const value = a.provider === 'imap' ? 'smtp' : a.provider;
              return <option key={value} value={value}>{label}</option>;
            })}
          </select>
          <div className="text-[10px] text-[#8E8FA5] mt-1">
            Tous les emails envoyés depuis OFManager (manuels et automatiques) passeront par ce compte.
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Wave 1u3 Phase D — Admin consent banner.
 *
 * Renders the "Activer Microsoft pour mon organisation" block. Two states :
 *   - Pas encore consentement : encart orange avec 2 boutons (lancer / copier le lien).
 *   - Au moins un consentement enregistré : encart vert récapitulatif.
 */
function AdminConsentBanner({ consents = [], busy, copied, onLaunch, onCopy }) {
  const hasConsent = Array.isArray(consents) && consents.length > 0;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };
  return (
    <div className="mt-3 pt-3 border-t border-[#F3F4F8]" data-testid="microsoft-admin-consent-banner">
      <div className="flex items-start gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${hasConsent ? 'bg-[#ECFDF5]' : 'bg-[#FFF7ED]'}`}>
          {hasConsent
            ? <ShieldCheck className="w-4 h-4 text-[#15803D]" />
            : <Building2 className="w-4 h-4 text-[#C2410C]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-bold text-[#1A1B2E]">
            Configuration Microsoft pour votre organisation
          </div>
          {hasConsent ? (
            <>
              <div className="text-[11.5px] text-[#15803D] mt-0.5">
                ✓ Activé pour {consents.length === 1 ? 'le tenant suivant' : `${consents.length} tenants`} :
              </div>
              <ul className="text-[11px] text-[#4A4B63] mt-1 space-y-0.5" data-testid="microsoft-admin-consent-list">
                {consents.map((c) => (
                  <li key={c.tenant_id} className="flex items-center justify-between gap-2">
                    <span className="font-medium">{c.tenant_hint || c.tenant_id}</span>
                    <span className="text-[10.5px] text-[#8E8FA5]">depuis le {fmtDate(c.granted_at)}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[10.5px] text-[#8E8FA5] mt-1.5 italic">
                Tous les utilisateurs de ces tenants peuvent utiliser OFManager avec lecture, écriture, marquage lu/non-lu et calendrier — sans approbation individuelle.
              </div>
              <button
                type="button"
                onClick={onLaunch}
                disabled={busy}
                data-testid="microsoft-admin-consent-relaunch"
                className="mt-2 h-7 px-2 rounded text-[11px] font-semibold border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] hover:bg-[#D1FAE5] disabled:opacity-50 inline-flex items-center gap-1"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                Renouveler l'approbation
              </button>
            </>
          ) : (
            <>
              <div className="text-[11.5px] text-[#4A4B63] mt-0.5">
                Pour permettre à <strong>tous les utilisateurs</strong> de votre organisation de marquer leurs emails comme lus/non-lus depuis OFManager, un administrateur Microsoft 365 doit approuver l'application <strong>une seule fois</strong>.
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={onLaunch}
                  disabled={busy}
                  data-testid="microsoft-admin-consent-launch"
                  className="h-8 px-3 rounded-lg bg-[#0078D4] text-white text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-[#106EBE] disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                  Je suis admin — approuver maintenant
                </button>
                <button
                  type="button"
                  onClick={onCopy}
                  disabled={busy}
                  data-testid="microsoft-admin-consent-copy"
                  className="h-8 px-3 rounded-lg border border-[#C7D2FE] bg-white text-[#4F46E5] text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-[#EEF2FF] disabled:opacity-50"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copied ? 'Lien copié !' : 'Copier le lien pour mon admin'}
                </button>
              </div>
              <details className="mt-2 text-[10.5px] text-[#6B6C85]" data-testid="microsoft-admin-consent-details">
                <summary className="cursor-pointer font-semibold hover:text-[#4F46E5]">
                  📋 Que se passe-t-il pour mon admin ?
                </summary>
                <div className="mt-1.5 pl-3 border-l-2 border-[#EFF0F6] space-y-1">
                  <div>1. Microsoft affiche un écran listant les permissions demandées par OFManager (Mail.ReadWrite, Mail.Send, Calendars.ReadWrite).</div>
                  <div>2. L'admin clique <strong>« Accepter »</strong> — l'approbation vaut pour TOUS les utilisateurs de votre tenant Microsoft 365.</div>
                  <div>3. Microsoft redirige vers OFManager qui confirme l'activation.</div>
                  <div>4. Tous vos collègues peuvent ensuite cliquer <em>« Connecter Outlook »</em> sans aucun blocage.</div>
                </div>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
